import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { formatDateOnly } from '../utils/dateFormatter';
import '../bootstrap-icons.css';
import styles from './TicketDetailsModal.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

function formatDuration(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatTime(dateString) {
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

/**
 * Setzt den globalen axios Auth-Header (falls Token vorhanden).
 */
function useAuthHeader() {
  return useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);
}

/**
 * Baut aus ticket.steps + ticket.stepTimestamps eine Liste von Items
 * { text, createdAt } im gleichen Index-Alignment.
 */
function buildItems(texts = [], timestamps = []) {
  return (texts).map((text, i) => ({
    text,
    createdAt: timestamps[i]?.createdAt || null,
  }));
}

function TicketDetailsModal({ isOpen, ticket, onClose, activeTimers, onStartTimer, onStopTimer }) {
  const ensureAuth = useAuthHeader();

  const [stepItems, setStepItems] = useState([]);
  const [todoItems, setTodoItems] = useState([]);
  const [newStep, setNewStep] = useState('');
  const [newTodo, setNewTodo] = useState('');
  const [editingStepIdx, setEditingStepIdx] = useState(null);
  const [editingStepText, setEditingStepText] = useState('');
  const [editingTodoIdx, setEditingTodoIdx] = useState(null);
  const [editingTodoText, setEditingTodoText] = useState('');
  const [editingDateIdx, setEditingDateIdx] = useState(null);

  const [timeEntries, setTimeEntries] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [isLoadingTime, setIsLoadingTime] = useState(false);

  const stepListRef = useRef(null);
  const todoListRef = useRef(null);
  const listBusyRef = useRef(false); // Guard: keine parallelen/doppelten Todo-Step-Mutationen

  // === Initialisierung aus ticket beim Oeffnen ===
  // Resync nur bei Open/Ticketwechsel: Das ticket-Objekt bekommt bei jedem
  // Socket-Refetch eine neue Identität - ein Resync dabei würde lokale
  // optimistische Änderungen zurückrollen (Todo doppelt/verschwunden).
  const ticketId = ticket?._id;
  useEffect(() => {
    if (isOpen && ticket) {
      setStepItems(buildItems(ticket.steps, ticket.stepTimestamps));
      setTodoItems(buildItems(ticket.todos, ticket.todoTimestamps));
      setNewStep(''); setNewTodo('');
      setEditingStepIdx(null); setEditingTodoIdx(null); setEditingDateIdx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticketId]);

  // === Time Entries laden ===
  useEffect(() => {
    const load = async () => {
      if (!isOpen || !ticket) return;
      setIsLoadingTime(true);
      try {
        ensureAuth();
        const resp = await axios.get(`${API_URL}/timetracking/ticket/${ticket._id}`);
        setTimeEntries(resp.data.entries || []);
        setTotalTime(resp.data.totalDuration || 0);
      } catch (e) {
        console.error('Error loading time entries:', e);
        setTimeEntries([]); setTotalTime(0);
      } finally { setIsLoadingTime(false); }
    };
    load();
  }, [isOpen, ticket, ensureAuth]);

  // === Persistenz-Helfer: schickt immer items + timestamps synchron ===
  const persist = useCallback(async (payload) => {
    ensureAuth();
    try {
      await axios.put(`${API_URL}/tickets/${ticket._id}`, payload);
    } catch (e) {
      console.error('Error persisting ticket:', e);
    }
  }, [ticket, ensureAuth]);

  const persistSteps = useCallback((items) => {
    const steps = items.map(it => it.text);
    const stepTimestamps = items.map((it, i) => ({
      text: it.text,
      createdAt: it.createdAt || new Date().toISOString(),
      index: i,
    }));
    return persist({ steps, stepTimestamps });
  }, [persist]);

  const persistTodos = useCallback((items) => {
    const todos = items.map(it => it.text);
    const todoTimestamps = items.map((it, i) => ({
      text: it.text,
      createdAt: it.createdAt || new Date().toISOString(),
      index: i,
    }));
    return persist({ todos, todoTimestamps });
  }, [persist]);

  // Atomare Persistierung: todos UND steps in einem PUT — verhindert Mischzustaende
  const persistLists = useCallback(async (newTodos, newSteps) => {
    const mk = (items) => items.map((it, i) => ({
      text: it.text,
      createdAt: it.createdAt || new Date().toISOString(),
      index: i,
    }));
    await persist({
      todos: newTodos.map(it => it.text),
      todoTimestamps: mk(newTodos),
      steps: newSteps.map(it => it.text),
      stepTimestamps: mk(newSteps),
    });
  }, [persist]);

  // === STEP ACTIONS ===
  const handleAddStep = async () => {
    const text = newStep.trim();
    if (!text || listBusyRef.current) return;
    listBusyRef.current = true;
    const updated = [...stepItems, { text, createdAt: new Date().toISOString() }];
    setStepItems(updated);
    setNewStep('');
    try {
      await persistLists(todoItems, updated);
    } finally {
      listBusyRef.current = false;
    }
    // Neueste Steps stehen zuoberst (Fix 30.10.2026: Sortierung nach Datum)
    requestAnimationFrame(() => {
      stepListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleEditStep = (idx) => {
    setEditingStepIdx(idx);
    setEditingStepText(stepItems[idx].text);
  };

  const handleSaveStepEdit = async () => {
    if (editingStepIdx === null || listBusyRef.current) return;
    const text = editingStepText.trim();
    if (!text) { setEditingStepIdx(null); return; }
    listBusyRef.current = true;
    const updated = stepItems.map((it, i) => i === editingStepIdx ? { ...it, text } : it);
    setStepItems(updated);
    setEditingStepIdx(null);
    try {
      await persistLists(todoItems, updated);
    } finally {
      listBusyRef.current = false;
    }
  };

  const handleDeleteStep = async (idx) => {
    if (listBusyRef.current) return;
    if (!window.confirm('Step löschen?')) return;
    listBusyRef.current = true;
    setEditingStepIdx(null);
    const updatedSteps = stepItems.filter((_, i) => i !== idx);
    setStepItems(updatedSteps);
    try {
      await persistLists(todoItems, updatedSteps);
    } finally {
      listBusyRef.current = false;
    }
  };

  const handleStepDateChange = async (idx, isoString) => {
    if (listBusyRef.current) return;
    listBusyRef.current = true;
    const updated = stepItems.map((it, i) => i === idx ? { ...it, createdAt: isoString } : it);
    setStepItems(updated);
    setEditingDateIdx(null);
    try {
      await persistLists(todoItems, updated);
    } finally {
      listBusyRef.current = false;
    }
  };

  // Step wiederherstellen -> zurück zu Todos, createdAt bleibt erhalten
  const handleRestoreStep = async (idx) => {
    if (listBusyRef.current) return;
    const step = stepItems[idx];
    if (!step) return;
    listBusyRef.current = true;
    setEditingStepIdx(null);
    const newSteps = stepItems.filter((_, i) => i !== idx);
    const newTodos = [...todoItems, { text: step.text, createdAt: step.createdAt || new Date().toISOString() }];
    setStepItems(newSteps);
    setTodoItems(newTodos);
    try {
      await persistLists(newTodos, newSteps);
    } finally {
      listBusyRef.current = false;
    }
  };

  // === TODO ACTIONS ===
  const handleAddTodo = async () => {
    const text = newTodo.trim();
    if (!text || listBusyRef.current) return;
    listBusyRef.current = true;
    const updated = [...todoItems, { text, createdAt: new Date().toISOString() }];
    setTodoItems(updated);
    setNewTodo('');
    try {
      await persistLists(updated, stepItems);
    } finally {
      listBusyRef.current = false;
    }
    requestAnimationFrame(() => {
      todoListRef.current?.scrollTo({ top: todoListRef.current.scrollHeight, behavior: 'smooth' });
    });
  };

  const handleEditTodo = (idx) => {
    setEditingTodoIdx(idx);
    setEditingTodoText(todoItems[idx].text);
  };

  const handleSaveTodoEdit = async () => {
    if (editingTodoIdx === null || listBusyRef.current) return;
    const text = editingTodoText.trim();
    if (!text) { setEditingTodoIdx(null); return; }
    listBusyRef.current = true;
    const updated = todoItems.map((it, i) => i === editingTodoIdx ? { ...it, text } : it);
    setTodoItems(updated);
    setEditingTodoIdx(null);
    try {
      await persistLists(updated, stepItems);
    } finally {
      listBusyRef.current = false;
    }
  };

  const handleDeleteTodo = async (idx) => {
    if (listBusyRef.current) return;
    if (!window.confirm('Todo löschen?')) return;
    listBusyRef.current = true;
    setEditingTodoIdx(null);
    const updated = todoItems.filter((_, i) => i !== idx);
    setTodoItems(updated);
    try {
      await persistLists(updated, stepItems);
    } finally {
      listBusyRef.current = false;
    }
  };

  // Todo abschliessen -> wird zu Step (Historie), createdAt bleibt erhalten
  const handleCompleteTodo = async (idx) => {
    if (listBusyRef.current) return;
    const todo = todoItems[idx];
    if (!todo) return;
    listBusyRef.current = true;
    // Falls dieses Todo gerade im Text-Edit steht, uebernehmen wir den aktuellen Edit-Text
    const wasEditing = editingTodoIdx === idx;
    const editedText = wasEditing ? editingTodoText.trim() : '';
    setEditingTodoIdx(null);
    const newTodos = todoItems.filter((_, i) => i !== idx);
    const newSteps = [...stepItems, { text: (wasEditing && editedText) ? editedText : todo.text, createdAt: todo.createdAt || new Date().toISOString() }];
    setTodoItems(newTodos);
    setStepItems(newSteps);
    try {
      await persistLists(newTodos, newSteps);
    } finally {
      listBusyRef.current = false;
    }
    requestAnimationFrame(() => {
      stepListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  // === Render ===
  if (!isOpen || !ticket) return null;

  // Fix 30.10.2026: Steps nach createdAt sortieren (neueste zuoberst). Die
  // Array-Reihenfolge ignorierte die Zeitsortierung - KI-/neue Steps landeten
  // bei langen Historien ganz unten. idx haelt die Original-Position fuer
  // Edit/Delete/Datum-Aenderung (steps und stepTimestamps sind index-gekoppelt).
  const sortedStepItems = stepItems
    .map((it, idx) => ({ ...it, idx }))
    .sort((a, b) => {
      const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (bd !== ad) return bd - ad;
      return a.idx - b.idx; // gleiche Timestamps: Einfuege-Reihenfolge halten
    });

  const projectName = ticket.projectRef?.name || ticket.project || 'Kein Projekt';

  return (
    <div className={styles.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal} role="dialog" aria-label={`Ticket: ${ticket.title}`}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Ticket Details</h2>
          <button className={styles.iconButton} onClick={onClose} aria-label="Schliessen">×</button>
        </div>

        <div className={styles.body}>
          {/* === Info === */}
          <div className={styles.section}>
            <div className={styles.infoGrid}>
              <span className={styles.infoLabel}>Titel</span>
              <span className={styles.infoValue}>{ticket.title}</span>
              <span className={styles.infoLabel}>Beschreibung</span>
              <span className={styles.infoValue}>{ticket.description || '—'}</span>
              <span className={styles.infoLabel}>Fällig</span>
              <span className={styles.infoValue}>{ticket.dueDate || '—'}</span>
              <span className={styles.infoLabel}>Projekt</span>
              <span className={styles.infoValue}>{projectName}</span>
              <span className={styles.infoLabel}>Priorität</span>
              <span className={styles.infoValue}>{ticket.priority || '—'}</span>
            </div>

            {ticket.isZohoTicket && (
              <div className={styles.zohoBox}>
                <p className={styles.zohoTitle}>Zoho Desk Ticket</p>
                {ticket.zohoCustomerName && <p className={styles.zohoLine}><strong>Kunde:</strong> {ticket.zohoCustomerName}</p>}
                {ticket.zohoCustomerEmail && <p className={styles.zohoLine}><strong>E-Mail:</strong> {ticket.zohoCustomerEmail}</p>}
                {ticket.zohoTicketUrl && (
                  <a className={styles.zohoLink} href={ticket.zohoTicketUrl} target="_blank" rel="noopener noreferrer">
                    In Zoho Desk öffnen <span style={{ fontSize: '10px' }}>↗</span>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* === Time Tracking === */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Zeiterfassung</h3>
            {isLoadingTime ? (
              <p className={styles.loadingText}>Lade Zeiteinträge…</p>
            ) : (
              <div className={styles.timeTotal}>
                <p className={styles.timeTotalValue}>Total: {formatDuration(totalTime)}</p>
              </div>
            )}
            {timeEntries.length > 0 && (
              <div className={styles.timeEntries}>
                {timeEntries.slice(0, 5).map((entry, i) => (
                  <div key={i} className={styles.timeEntry}>
                    <div className={styles.timeEntryRow}>
                      <span className={styles.timeEntryTime}>
                        {formatTime(entry.startTime)}
                        {entry.endTime ? ` – ${formatTime(entry.endTime)}` : ' (läuft…)'}
                      </span>
                      <span className={styles.timeEntryDuration}>{formatDuration(entry.duration)}</span>
                    </div>
                    {entry.stepText && <div className={styles.timeEntryStep}>Step: {entry.stepText}</div>}
                    {entry.description && <div className={styles.timeEntryDesc}>{entry.description}</div>}
                  </div>
                ))}
                {timeEntries.length > 5 && (
                  <div className={styles.timeMore}>… {timeEntries.length - 5} weitere Einträge</div>
                )}
              </div>
            )}
          </div>

          {/* === Todos (offene Aufgaben) === */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Todos
              {todoItems.length > 0 && <span className={styles.sectionBadge}>{todoItems.length}</span>}
            </h3>
            <div className={styles.list} ref={todoListRef}>
              {todoItems.length === 0 && <p className={styles.emptyState}>Keine offenen Todos.</p>}
              {todoItems.map((item, idx) => (
                <div key={`todo-${idx}`} className={styles.item}>
                  <div className={styles.itemMain} style={{ flex: 1 }}>
                    {editingTodoIdx === idx ? (
                      <input
                        className={styles.editInput}
                        value={editingTodoText}
                        autoFocus
                        onChange={(e) => setEditingTodoText(e.target.value)}
                        onBlur={handleSaveTodoEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSaveTodoEdit(); }
                          if (e.key === 'Escape') setEditingTodoIdx(null);
                        }}
                      />
                    ) : (
                      <>
                        <span className={styles.itemText} style={{ cursor: 'pointer' }} onDoubleClick={() => handleEditTodo(idx)} title="Doppelklick zum Bearbeiten → dann Löschen möglich">{item.text}</span>
                        {item.createdAt && <span className={styles.itemDate}>erstellt {formatDateOnly(item.createdAt)}</span>}
                      </>
                    )}
                  </div>
                  <div className={styles.itemActions} onMouseDown={(e) => e.preventDefault()}>
                    <button className={`${styles.actionBtn} ${styles.actionBtnDone}`} onClick={() => handleCompleteTodo(idx)} title="Als erledigt markieren → wandert zu Steps">Done</button>
                    {editingTodoIdx === idx && (
                      <button className={`${styles.actionBtn} ${styles.actionBtnDelete}`} onClick={() => handleDeleteTodo(idx)} title="Löschen">Löschen</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.addRow}>
              <input
                className={styles.addInput}
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                placeholder="Neues Todo…"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTodo(); } }}
              />
              <button className={styles.addBtn} onClick={handleAddTodo} disabled={!newTodo.trim()}>Hinzufügen</button>
            </div>
          </div>

          {/* === Steps (Historie) === */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Steps (Verlauf)
              {stepItems.length > 0 && <span className={styles.sectionBadge}>{stepItems.length}</span>}
            </h3>
            <div className={styles.list} ref={stepListRef} style={{ maxHeight: '340px', overflowY: 'auto' }}>
              {stepItems.length === 0 && <p className={styles.emptyState}>Noch keine Steps erfasst.</p>}
              {sortedStepItems.map((item) => (
                <div key={`step-${item.idx}`} className={styles.item}>
                  <div className={styles.itemMain} style={{ flex: 1 }}>
                    {editingStepIdx === item.idx ? (
                      <input
                        className={styles.editInput}
                        value={editingStepText}
                        autoFocus
                        onChange={(e) => setEditingStepText(e.target.value)}
                        onBlur={handleSaveStepEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleSaveStepEdit(); }
                          if (e.key === 'Escape') setEditingStepIdx(null);
                        }}
                      />
                    ) : editingDateIdx === item.idx ? (
                      <input
                        type="date"
                        className={styles.datePicker}
                        autoFocus
                        defaultValue={item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) handleStepDateChange(item.idx, new Date(v + 'T12:00:00').toISOString());
                        }}
                        onBlur={() => setEditingDateIdx(null)}
                      />
                    ) : (
                      <>
                        <span className={styles.itemText} style={{ cursor: 'pointer' }} onDoubleClick={() => handleEditStep(item.idx)} title="Doppelklick zum Bearbeiten → dann Löschen möglich">{item.text}</span>
                        <span
                          className={styles.itemDate}
                          onClick={() => setEditingDateIdx(item.idx)}
                          style={{ cursor: 'pointer' }}
                          title="Klicken zum Ändern des Datums"
                        >
                          {item.createdAt ? formatDateOnly(item.createdAt) : 'Kein Datum'}
                        </span>
                      </>
                    )}
                  </div>
                  <div className={styles.itemActions} onMouseDown={(e) => e.preventDefault()}>
                    {editingDateIdx !== item.idx && (
                      <button className={`${styles.actionBtn} ${styles.actionBtnRestore}`} onClick={() => handleRestoreStep(item.idx)} title="Wiederherstellen → zurück zu Todos">Wiederherstellen</button>
                    )}
                    {editingStepIdx === item.idx && (
                      <button className={`${styles.actionBtn} ${styles.actionBtnDelete}`} onClick={() => handleDeleteStep(item.idx)} title="Löschen">Löschen</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.addRow}>
              <input
                className={styles.addInput}
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                placeholder="Neuer Step (erledigte Arbeit)…"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddStep(); } }}
              />
              <button className={styles.addBtn} onClick={handleAddStep} disabled={!newStep.trim()}>Hinzufügen</button>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.closeBtn} onClick={onClose}>Schliessen</button>
        </div>
      </div>
    </div>
  );
}

export default TicketDetailsModal;
